import { useState } from 'react';
import { Star, Loader } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import Toast from '../common/Toast';
import axios from 'axios';

/**
 * ReviewFormDrawer
 * Right-side drawer for writing business reviews
 * 
 * Props:
 * - businessId: string - ID of the business being reviewed
 * - isOpen: boolean - Whether drawer is visible
 * - onClose: () => void - Called when drawer should close
 * - onReviewSubmitted: () => void - Called after successful review submission
 */
const ReviewFormDrawer = ({ businessId, isOpen, onClose, onReviewSubmitted }) => {
  const { user, accessToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [hoveredRating, setHoveredRating] = useState(0);

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    rating: 0,
    experience_date: new Date().toISOString().split('T')[0],
    media_urls: []
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRatingChange = (rating) => {
    setFormData(prev => ({
      ...prev,
      rating
    }));
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setToast({ type: 'error', message: 'Review title is required' });
      return false;
    }
    if (formData.title.length < 5) {
      setToast({ type: 'error', message: 'Title must be at least 5 characters' });
      return false;
    }
    if (formData.title.length > 255) {
      setToast({ type: 'error', message: 'Title cannot exceed 255 characters' });
      return false;
    }

    if (!formData.content.trim()) {
      setToast({ type: 'error', message: 'Review content is required' });
      return false;
    }
    if (formData.content.length < 10) {
      setToast({ type: 'error', message: 'Content must be at least 10 characters' });
      return false;
    }
    if (formData.content.length > 5000) {
      setToast({ type: 'error', message: 'Content cannot exceed 5000 characters' });
      return false;
    }

    if (formData.rating < 1 || formData.rating > 5) {
      setToast({ type: 'error', message: 'Please select a rating' });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL || 'https://vmc-backend-vt2w.onrender.com/api'}/reviews`,
        {
          business_id: businessId,
          title: formData.title,
          content: formData.content,
          rating: formData.rating,
          experience_date: formData.experience_date,
          media_urls: formData.media_urls
        },
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setToast({ type: 'success', message: 'Review submitted successfully!' });
      setFormData({
        title: '',
        content: '',
        rating: 0,
        experience_date: new Date().toISOString().split('T')[0],
        media_urls: []
      });

      // Notify parent to refresh reviews
      if (onReviewSubmitted) {
        onReviewSubmitted();
      }

      // Close modal after 1 second
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      console.error('Error submitting review:', error);
      const errorMessage = error.response?.data?.message || 'Failed to submit review. Please try again.';
      setToast({ type: 'error', message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/30 z-30 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed right-0 top-0 bottom-0 w-full max-w-[500px] bg-white shadow-xl z-40 transform transition-all duration-300 flex flex-col
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* ── Header ── */}
        <div className="flex-shrink-0 border-b border-slate-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-[15px] font-bold text-slate-900">Write a Review</h2>
            <button
              onClick={onClose}
              disabled={loading}
              className="p-1 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* ── Content (Scrollable) ── */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <form onSubmit={handleSubmit} className="space-y-6" id="review-form">
            {/* Rating Selection */}
            <div>
              <label className="block text-[12px] font-semibold text-slate-900 mb-3">
                Rating *
              </label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map(rating => (
                  <button
                    key={rating}
                    type="button"
                    onClick={() => handleRatingChange(rating)}
                    onMouseEnter={() => setHoveredRating(rating)}
                    onMouseLeave={() => setHoveredRating(0)}
                    className="transition transform hover:scale-110"
                    disabled={loading}
                  >
                    <Star
                      size={32}
                      className={
                        rating <= (hoveredRating || formData.rating)
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-300'
                      }
                    />
                  </button>
                ))}
              </div>
              {formData.rating > 0 && (
                <p className="text-[12px] text-slate-600 mt-2">
                  You rated this {formData.rating} out of 5 stars
                </p>
              )}
            </div>

            {/* Title */}
            <div>
              <label className="block text-[12px] font-semibold text-slate-900 mb-2">
                Review Title * ({formData.title.length}/255)
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Summarize your experience..."
                maxLength="255"
                className="w-full px-4 py-2 text-[13px] border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              />
            </div>

            {/* Content */}
            <div>
              <label className="block text-[12px] font-semibold text-slate-900 mb-2">
                Review Details * ({formData.content.length}/5000)
              </label>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                placeholder="Share your detailed experience with this business..."
                maxLength="5000"
                rows="5"
                className="w-full px-4 py-2 text-[13px] border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                disabled={loading}
              />
            </div>

            {/* Experience Date */}
            <div>
              <label className="block text-[12px] font-semibold text-slate-900 mb-2">
                Experience Date
              </label>
              <input
                type="date"
                name="experience_date"
                value={formData.experience_date}
                onChange={handleInputChange}
                max={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-2 text-[13px] border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              />
            </div>
          </form>
        </div>

        {/* ── Footer (Sticky) ── */}
        <div className="flex-shrink-0 border-t border-slate-200 px-6 py-4 bg-slate-50 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2 text-[12px] font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="review-form"
            disabled={loading || formData.rating < 1}
            className="flex-1 px-4 py-2 text-[12px] font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading && <Loader size={16} className="animate-spin" />}
            {loading ? 'Submitting...' : 'Submit Review'}
          </button>
        </div>
      </div>

      {/* Toast Notification */}
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default ReviewFormDrawer;
